/* eslint-disable @wyz/module/no-relative-import */
import { notify } from '@wyz/ui';
import * as React from 'react';
import { useApplicationContext } from '~/common/context';
import {
  CustomEventConfig,
  PluginConfig,
  PluginContextType,
} from '~/common/types';
import { EventListenerManager } from '~/common/helpers';
import { usePluginEventHandlers } from '~/common/hooks';
import { PluginContext } from './contexts';

export const usePlugin = () => {
  const context = React.useContext(PluginContext);
  if (!context) {
    throw new Error('usePlugin must be used within PluginProvider');
  }
  return context;
};

type EnabledPlugins = {
  navbar: boolean;
  footer: boolean;
  sidebar: boolean;
};

type PluginState =
  | { status: 'idle' }
  | { status: 'loading-config'; configUrl: string }
  | { status: 'loading-script'; scriptUrl: string; config: PluginConfig }
  | {
      status: 'ready';
      enabledPlugins: EnabledPlugins;
      api: PluginContextType['api'];
      config: PluginConfig;
    }
  | { status: 'error'; error: Error };

type PluginAction =
  | { type: 'LOAD_CONFIG'; configUrl: string }
  | { type: 'CONFIG_LOADED'; config: PluginConfig }
  | { type: 'LOAD_SCRIPT'; scriptUrl: string }
  | { type: 'UNLOAD' }
  | {
      type: 'PLUGIN_READY';
      enabledPlugins: EnabledPlugins;
      api: PluginContextType['api'];
    }
  | { type: 'ERROR'; error: Error };

type PluginProviderProps = {
  children: React.ReactNode;
};

const SCRIPT_ID = 'external-plugin-script';
const PLUGIN_CHECK_TIMEOUT = 50;
const PLUGIN_CHECK_MAX_ATTEMPTS = 100;

const pluginReducer = (
  state: PluginState,
  action: PluginAction,
): PluginState => {
  switch (action.type) {
    case 'LOAD_CONFIG':
      return { status: 'loading-config', configUrl: action.configUrl };
    case 'CONFIG_LOADED':
      if (action.config.scriptUrl) {
        return {
          status: 'loading-script',
          scriptUrl: action.config.scriptUrl,
          config: action.config,
        };
      }
      // No script URL, go to ready state without plugin
      return {
        status: 'ready',
        enabledPlugins: { navbar: false, footer: false, sidebar: false },
        api: {},
        config: action.config,
      };
    case 'LOAD_SCRIPT':
      return state.status === 'loading-script'
        ? state
        : { status: 'loading-script', scriptUrl: action.scriptUrl, config: {} };
    case 'UNLOAD':
      return { status: 'idle' };
    case 'PLUGIN_READY':
      return {
        status: 'ready',
        enabledPlugins: action.enabledPlugins,
        api: action.api,
        config: state.status === 'loading-script' ? state.config : {},
      };
    case 'ERROR':
      return { status: 'error', error: action.error };
    default:
      return state;
  }
};

const cleanupPlugin = () => {
  const existingScript = document.getElementById(SCRIPT_ID);
  existingScript?.remove();
  if (window.LayoutPlugin) {
    delete window.LayoutPlugin;
  }
};

// Authors note: Added cache busting to ensure fresh script load, this is also important for testing the feature
const addCacheBuster = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_t=${Date.now()}`;
};

const getEnabledPlugins = (): EnabledPlugins => ({
  navbar: customElements.get('custom-navbar') !== undefined,
  footer: customElements.get('custom-footer') !== undefined,
  sidebar: customElements.get('custom-sidebar') !== undefined,
});

export const PluginProvider: React.FC<PluginProviderProps> = ({ children }) => {
  const { application } = useApplicationContext();
  const pluginConfigUrl = application?.pluginConfigUrl;
  const { getEventHandler } = usePluginEventHandlers();

  const [state, dispatch] = React.useReducer(pluginReducer, { status: 'idle' });
  const [isTestingPlugin, setIsTestingPlugin] = React.useState(false);
  const [activeConfigUrl, setActiveConfigUrl] = React.useState<
    string | undefined
  >(pluginConfigUrl);

  const lastLoadedUrlRef = React.useRef<string | undefined>(undefined);
  const eventListenerManagerRef = React.useRef<EventListenerManager>(
    new EventListenerManager(),
  );

  const registerCustomEventHandlers = React.useCallback(
    (customEvents: CustomEventConfig[]) => {
      customEvents.forEach((eventConfig) => {
        const handler = getEventHandler(eventConfig.name);

        if (!handler) {
          return;
        }

        eventListenerManagerRef.current.registerListener(eventConfig, handler);
      });
    },
    [getEventHandler],
  );

  const checkPluginReady = React.useCallback(() => {
    let attempts = 0;

    const check = () => {
      if (window.LayoutPlugin) {
        try {
          window.LayoutPlugin.define();
          dispatch({
            type: 'PLUGIN_READY',
            enabledPlugins: getEnabledPlugins(),
            api: {
              onAddToCart: window.LayoutPlugin?.onAddToCart,
            },
          });
        } catch (error) {
          dispatch({ type: 'ERROR', error: error as Error });
          eventListenerManagerRef.current.removeAllListeners();
        }
      } else if (attempts < PLUGIN_CHECK_MAX_ATTEMPTS) {
        attempts++;
        setTimeout(check, PLUGIN_CHECK_TIMEOUT);
      } else {
        const error = new Error('Plugin not available after loading script');
        dispatch({ type: 'ERROR', error });
        eventListenerManagerRef.current.removeAllListeners();
      }
    };

    check();
  }, []);

  const loadScript = React.useCallback(
    (scriptUrl: string) => {
      const script = document.createElement('script');
      script.src = addCacheBuster(scriptUrl);
      script.id = SCRIPT_ID;
      script.type = 'module';
      script.async = true;

      script.onload = () => checkPluginReady();
      script.onerror = () => {
        const error = new Error(`Failed to load external script: ${scriptUrl}`);
        notify.error(error.message, {
          id: 'toast-error-plugin-script-load-failed',
        });
        dispatch({ type: 'ERROR', error });
        eventListenerManagerRef.current.removeAllListeners();
      };

      document.head.appendChild(script);
    },
    [checkPluginReady],
  );

  const loadConfig = React.useCallback(
    async (configUrl: string) => {
      try {
        const response = await fetch(addCacheBuster(configUrl));

        if (!response.ok) {
          throw new Error(
            `Failed to fetch plugin config: ${response.statusText}`,
          );
        }

        const config: PluginConfig = await response.json();

        if (config.customEvents && config.customEvents.length > 0) {
          registerCustomEventHandlers(config.customEvents);
        }

        dispatch({ type: 'CONFIG_LOADED', config });

        if (config.scriptUrl) {
          dispatch({ type: 'LOAD_SCRIPT', scriptUrl: config.scriptUrl });
          loadScript(config.scriptUrl);
        } else {
          eventListenerManagerRef.current.removeAllListeners();
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        notify.error(err.message, {
          id: 'toast-error-plugin-config-load-failed',
        });
        dispatch({ type: 'ERROR', error: err });
        eventListenerManagerRef.current.removeAllListeners();
      }
    },
    [loadScript, registerCustomEventHandlers],
  );

  React.useEffect(() => {
    setActiveConfigUrl(pluginConfigUrl);
  }, [pluginConfigUrl]);

  React.useEffect(() => {
    if (!activeConfigUrl) {
      if (lastLoadedUrlRef.current) {
        cleanupPlugin();
        eventListenerManagerRef.current.removeAllListeners();
        dispatch({ type: 'UNLOAD' });
        lastLoadedUrlRef.current = undefined;
      }
      return;
    }

    if (lastLoadedUrlRef.current === activeConfigUrl) {
      return;
    }

    if (lastLoadedUrlRef.current) {
      cleanupPlugin();
      eventListenerManagerRef.current.removeAllListeners();
    }

    lastLoadedUrlRef.current = activeConfigUrl;
    dispatch({ type: 'LOAD_CONFIG', configUrl: activeConfigUrl });
    void loadConfig(activeConfigUrl);
  }, [activeConfigUrl, loadConfig]);

  // cleanup on unmount
  React.useEffect(() => {
    const manager = eventListenerManagerRef.current;
    return () => {
      manager.removeAllListeners();
    };
  }, []);

  const testPlugin = React.useCallback((url: string) => {
    setIsTestingPlugin(true);
    setActiveConfigUrl(url);
  }, []);

  const resetPlugin = React.useCallback(() => {
    setIsTestingPlugin(false);
    setActiveConfigUrl(pluginConfigUrl);
  }, [pluginConfigUrl]);

  const contextValue = React.useMemo<PluginContextType>(() => {
    const baseState = {
      testPlugin,
      resetPlugin,
      isTestingPlugin,
      scriptUrl: activeConfigUrl,
      api: {},
      enabledEvents: [],
    };

    switch (state.status) {
      case 'idle':
        return {
          ...baseState,
          isPluginReady: true,
          pluginError: null,
          enabledPlugins: { navbar: false, footer: false, sidebar: false },
        };
      case 'loading-config':
      case 'loading-script':
        return {
          ...baseState,
          isPluginReady: false,
          pluginError: null,
          enabledPlugins: { navbar: false, footer: false, sidebar: false },
        };
      case 'ready':
        return {
          ...baseState,
          isPluginReady: true,
          pluginError: null,
          enabledPlugins: state.enabledPlugins,
          api: state.api,
          enabledEvents: state.config.customEvents?.map((e) => e.name) ?? [],
        };
      case 'error':
        return {
          ...baseState,
          isPluginReady: true,
          pluginError: state.error,
          enabledPlugins: { navbar: false, footer: false, sidebar: false },
        };
    }
  }, [state, testPlugin, resetPlugin, isTestingPlugin, activeConfigUrl]);

  return (
    <PluginContext.Provider value={contextValue}>
      {children}
    </PluginContext.Provider>
  );
};
